/**
 * 폴더 섹션과 퀴즈 섹션을 세로로 잇는 화면(내 퀴즈, 남의 프로필)이 무엇을 그릴지의 조합.
 *
 * 아직 정해지지 않음(`pending`)을 넣은 하나의 값으로 둔다 — 독립된 boolean 이면 `퀴즈 섹션`과 `전용 빈 화면`이
 * 동시에 켜지는 상태가 표현 가능해진다. 조합을 화면 섹션으로 옮기는 표는 화면마다 둔다.
 */
export type SectionView =
  | 'pending'
  | 'both'
  | 'foldersOnly'
  | 'quizzesOnly'
  | 'empty';

/** 두 목록을 다 받은 뒤의 조합. 로딩 중을 따로 다루지 않는 화면(남의 프로필)은 이것만 쓴다. */
export type LoadedSectionView = Exclude<SectionView, 'pending'>;

export const resolveLoadedSectionView = ({
  isSearching,
  hasFolders,
  hasQuizzes,
  hasCreateCard,
}: {
  isSearching: boolean;
  hasFolders: boolean;
  hasQuizzes: boolean;
  /**
   * 폴더 섹션에 `새 폴더` 카드가 있는가(내 퀴즈). 있으면 폴더가 0개여도 검색 전에는 섹션을 남긴다.
   * 없으면(남의 프로필) 보여 줄 폴더가 없을 때 섹션을 접는다.
   */
  hasCreateCard: boolean;
}): LoadedSectionView => {
  if (!isSearching) return hasCreateCard || hasFolders ? 'both' : 'quizzesOnly';

  if (hasFolders && hasQuizzes) return 'both';
  if (hasFolders) return 'foldersOnly';
  if (hasQuizzes) return 'quizzesOnly';
  return 'empty';
};

/**
 * 두 쿼리가 끝나기 전에는 아무것도 그리지 않는다(`pending`).
 * 섹션을 먼저 그리고 데이터가 도착할 때 채우면 그때마다 아래가 밀린다(CLS).
 *
 * 검색 refetch 는 `keepPreviousData` 로 이전 결과가 남아 이 구간에 걸리지 않는다.
 */
export const resolveSectionView = ({
  isLoading,
  ...loaded
}: Parameters<typeof resolveLoadedSectionView>[0] & {
  isLoading: boolean;
}): SectionView => (isLoading ? 'pending' : resolveLoadedSectionView(loaded));
